/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { PreliminaryQuizPageProps } from "./interfaces";
import { preliminaryQuizStyle } from "./styles";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSTR } from "react-str";
import { getData } from "../../utils/fetchers";
import { URL } from "../../API/urlEndpoints";
import { startQuiz, back, loadingScreenValues } from "../../constants/commons";
import * as _ from "lodash";
import {
  displayConfirmationWhenWindowCloses,
  showDisplayHeadingAndContentFromLoadingStatus,
} from "../../utils/helpers";
import { responsiveStyles } from "../../commons/styles";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import { CustomForm } from "../CustomForm";
import { ShowMessage } from "../../components/ShowMessage";
import {
  CustomButtonColor,
  CustomButtonTypes,
} from "../../atoms/CustomButton/interface";
import { useUserContext } from "../../hooks/cookieHooks";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import { CustomButton } from "../../atoms/CustomButton";
import uploadedDocsIcon from "../../assets/uploaded-docs-logo.svg";
import { Link } from "react-router-dom";

export const PreliminaryQuizPage: React.FunctionComponent<PreliminaryQuizPageProps> = (
  props
) => {
  const { onBackButtonClick } = props;
  const isMobileView = useIsMobile();
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // State for showing CSF component
  const [showCFSComponent, setShowCFSComponent] = React.useState<boolean>(
    false
  );
  const [showStartQuizModal, setShowStartQuizModal] = React.useState<boolean>(
    false
  );

  // On page load, for fetching the details of Preliminary Details Info
  const [
    ,
    {
      result: getPreliminaryQuizInfo,
      revalidator: refetchPreliminaryQuizInfo,
      loading,
      error: getPreliminaryQuizInfoError,
    },
  ] = useSTR(getData);

  React.useEffect(() => {
    refetchPreliminaryQuizInfo(
      `${URL.getPreliminaryQuizInfo(_.get(user, "empId"))}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isStudyMaterialPresent = React.useMemo(() => {
    return getPreliminaryQuizInfo?.studyMaterial;
  }, [getPreliminaryQuizInfo]);

  // Function to check whether to show the QUIZ form or not
  React.useEffect(() => {
    getPreliminaryQuizInfo?.isFormSubmitted && setShowCFSComponent(true);
  }, [getPreliminaryQuizInfo]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function which is called whewn the browser closes
  displayConfirmationWhenWindowCloses();

  const downloadFromSignedS3URL = (
    event: React.MouseEvent,
    preSignedURL: string
  ) => {
    event.preventDefault();
    window.open(preSignedURL);
  };

  const renderMaterialDetails = () => {
    return (
      isStudyMaterialPresent &&
      isStudyMaterialPresent.map((data: any, id: number) => {
        return (
          <div className="d-flex align-items-center my-4" key={id}>
            <img
              src={uploadedDocsIcon}
              alt=""
              className="responsive-pdf-thumbnail"
            />
            <div className="p-2">{data.name}</div>

            <Link
              to="route"
              target="_blank"
              className={`ml-auto p-2 download-style border-0 user-button-style ${
                isMobileView ? "" : "bg-white"
              }`}
              onClick={(event) => downloadFromSignedS3URL(event, data.fileUrl)}
            >
              Download
            </Link>
          </div>
        );
      })
    );
  };

  // Hook which is called whenever there is an error in either GET/POST calls
  const showErrorComponent = React.useMemo(() => {
    return getPreliminaryQuizInfoError;
  }, [getPreliminaryQuizInfoError]);

  return (
    <span css={[responsiveStyles, preliminaryQuizStyle]}>
      <ShowMessage
        visible={loading || showStartQuizModal}
        loading={loading ? true : false}
        heading={
          loading
            ? showDisplayHeadingAndContentFromLoadingStatus({
                preLoginScreen: loading,
                postLoginScreen: loading,
              })?.loadingHeading
            : loadingScreenValues.ATTENTION_HEADING_MESSAGE
        }
        description={
          loading
            ? showDisplayHeadingAndContentFromLoadingStatus({
                preLoginScreen: loading,
                postLoginScreen: loading,
              })?.loadingDescription
            : !!getPreliminaryQuizInfo?.studyMaterial?.length
            ? loadingScreenValues.READING_MATERIAL_MESSAGE
            : loadingScreenValues.QUIZ_START_WARNING_MESSAGE
        }
        footerButtons={
          loading
            ? []
            : [
                {
                  key: 1,
                  text: "Cancel",
                  color: CustomButtonColor.SECONDARY,
                  type: CustomButtonTypes.BUTTON,
                  onClick: (event: React.MouseEvent) => {
                    setShowStartQuizModal(false);
                  },
                },
                {
                  key: 2,
                  text: "Start Now",
                  color: CustomButtonColor.PRIMARY,
                  type: CustomButtonTypes.BUTTON,
                  onClick: (event: React.MouseEvent) => {
                    setShowStartQuizModal(false);
                    setShowCFSComponent(true);
                  },
                },
              ]
        }
        onCloseIconClick={() => {
          setShowStartQuizModal(false);
        }}
      />
      <RegistrationLayout>
        <h2>Preliminary Quiz</h2>

        {showCFSComponent ? (
          <CustomForm />
        ) : (
          <React.Fragment>
            <div className="my-3">
              <h4>This Quiz is to assess your knowledge on the field. </h4>
              {!!getPreliminaryQuizInfo?.studyMaterial?.length && (
                <div className="message-style">
                  We recommend you to go over the reading material provided
                  before attempting the quiz
                </div>
              )}
            </div>
            {!!getPreliminaryQuizInfo?.studyMaterial?.length && (
              <div className="mt-3">
                <h4>Self Read Material</h4>
                {renderMaterialDetails()}
              </div>
            )}
          </React.Fragment>
        )}
        {showErrorComponent && <ErrorComponent errorObj={showErrorComponent} />}
      </RegistrationLayout>

      {!showCFSComponent && (
        <div className="mt-3">
          <div
            className={`d-flex my-3 ${
              isMobileView ? "justify-content-center" : ""
            }`}
          >
            <CustomButton
              className="px-5"
              color={CustomButtonColor.DEFAULT}
              type={CustomButtonTypes.BUTTON}
              onClick={onBackButtonClick}
            >
              {back}
            </CustomButton>

            <CustomButton
              className="px-5 ml-auto"
              color={CustomButtonColor.PRIMARY}
              type={CustomButtonTypes.BUTTON}
              onClick={() => {
                setShowStartQuizModal(true);
              }}
            >
              {startQuiz}
            </CustomButton>
          </div>
        </div>
      )}
    </span>
  );
};
